import { useCallback, useEffect, useState } from 'react';
import {
  warehouseApi,
  type WarehouseResponse,
  type WmPickListResponse,
  type WmWave,
} from '@/api/warehouse.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
export function WarehouseWavesPage() {
  const [waves, setWaves] = useState<WmWave[]>([]);
  const [picks, setPicks] = useState<WmPickListResponse[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseResponse[]>([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [selectedPickIds, setSelectedPickIds] = useState<number[]>([]);

  const reload = useCallback(() => {
    Promise.all([warehouseApi.listWaves(), warehouseApi.listOpenPicks()])
      .then(([w, p]) => {
        setWaves(w);
        setPicks(p);
      })
      .catch((e) => showApiError(e, 'Failed to load waves'));
  }, []);

  useEffect(() => {
    reload();
    warehouseApi.listWarehouses().then(setWarehouses).catch(() => {});
  }, [reload]);

  const togglePick = (id: number) => {
    setSelectedPickIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const createWave = async () => {
    if (!warehouseId || selectedPickIds.length === 0) return;
    try {
      await warehouseApi.createWave({
        warehouseId: Number(warehouseId),
        pickListIds: selectedPickIds,
      });
      notify.success('Wave created');
      setSelectedPickIds([]);
      reload();
    } catch (e) {
      showApiError(e, 'Create wave failed');
    }
  };

  const releaseWave = async (id: number) => {
    try {
      await warehouseApi.releaseWave(id);
      notify.success('Wave released');
      reload();
    } catch (e) {
      showApiError(e, 'Release wave failed');
    }
  };

  return (
    <div className="pb-safe">
      <PageHeader
        title="Wave picking"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Warehouse', to: '/warehouse' },
          { label: 'Waves' },
        ]}
        actions={<Button variant="secondary" onClick={reload}>Refresh</Button>}
      />

      <div className="card mb-6 p-4">
        <h2 className="mb-3 font-semibold">Create wave</h2>
        <Select
          label="Warehouse"
          value={warehouseId}
          onChange={(v) => setWarehouseId(v)}
          options={[
            { value: '', label: 'Select…' },
            ...warehouses.map((w) => ({ value: String(w.id), label: w.code })),
          ]}
        />
        <p className="mt-3 text-sm text-slate-500">Select open pick lists to batch:</p>
        <ul className="mt-2 space-y-1 text-sm">
          {picks.map((p) => (
            <li key={p.id}>
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedPickIds.includes(p.id)}
                  onChange={() => togglePick(p.id)}
                />
                {p.pickCode} (WH {p.warehouseId})
              </label>
            </li>
          ))}
        </ul>
        <Button className="mt-3" onClick={() => void createWave()}>
          Create wave
        </Button>
      </div>

      <ul className="space-y-2">
        {waves.map((w) => (
          <li key={w.id} className="card flex items-center justify-between p-3">
            <span>
              {w.waveCode} · WH {w.warehouseId} · {w.statusCode}
            </span>
            {w.statusCode === 'PLANNED' && (
              <Button className="text-sm py-1 px-2" onClick={() => void releaseWave(w.id)}>
                Release
              </Button>
            )}
          </li>
        ))}
      </ul>
      {waves.length === 0 && <p className="text-sm text-slate-500">No waves yet.</p>}
    </div>
  );
}
